import arrayUtils from "@/common/utils/arrayUtils";
import * as sizeManager from "@/modules/sizeManager";
import {
  isImmersiveSize,
  IMMERSIVE_MAX_SHORT_SIDE,
} from "@/modules/landscapeImmersive";
import { isMcpLandscapeLayout } from "@/modules/mcpSurface";
import type { Size } from "@/common/utils/dimensionUtils";

export const orientationType = {
  UNKNOWN: "unknown",
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
} as const;

export type Orientation =
  (typeof orientationType)[keyof typeof orientationType];

export type OrientationListener = (orientation: Orientation) => void;

// Immersive landscape gate — the pure predicate lives in landscapeImmersive
// (unit-tested); this wrapper supplies the live viewport size when the caller
// doesn't pass one. Re-exported constant kept for callers/tests.
export { IMMERSIVE_MAX_SHORT_SIDE };

export function isImmersiveLandscapeSize(size?: Size | null): boolean {
  const s = size || sizeManager.getSize();
  // Pseudo-fullscreen lays a portrait phone out as landscape on purpose (see
  // calcOrientation), so the hold doesn't apply to it.
  if (
    heldOrientation &&
    !(typeof window !== "undefined" && window.__pixPseudoFs)
  ) {
    return heldOrientation === orientationType.LANDSCAPE && isPhoneScale(s);
  }
  return isImmersiveSize(s);
}

// Phone-scale in EITHER orientation (short side <= threshold). Unlike
// isImmersiveLandscapeSize (which is false in portrait by design), this detects a
// phone regardless of orientation — used to gate the fullscreen button + pseudo-
// fullscreen so they fire on a portrait phone but NOT a touchscreen laptop / iPad.
export function isPhoneScale(size?: Size | null): boolean {
  const s = size || sizeManager.getSize();
  return !!s && Math.min(s.width, s.height) <= IMMERSIVE_MAX_SHORT_SIDE;
}

// Chrome on iOS shrinks the viewport for the on-screen keyboard, so a portrait
// phone typing in Pixie measures wider than tall. Read by size, that flips the
// player to the landscape layout, which unmounts the field, closes the keyboard
// and flips back, over and over. So while a text field has focus on a touch phone, the
// orientation the frame had when focus arrived holds, for the layout and the
// immersive gate alike. That is the frame's shape, not the device's, so an
// inline embed keeps its own. The hold lets go once the viewport is back to that
// shape after the field loses focus (the keyboard has closed), on a real
// rotation, or after KEYBOARD_CLOSE_MS if the viewport never settles back.
const TEXT_INPUT_TYPES = /^(|text|search|email|url|tel|password|number)$/;
const KEYBOARD_CLOSE_MS = 1000;
let heldOrientation: Orientation | null = null;

function isTextField(el: HTMLInputElement | null): boolean {
  if (!el) {
    return false;
  }
  if (el.isContentEditable || el.tagName === "TEXTAREA") {
    return true;
  }
  return el.tagName === "INPUT" && TEXT_INPUT_TYPES.test(el.type || "");
}

function sizeOrientation(size: Size): Orientation {
  return size.width > size.height
    ? orientationType.LANDSCAPE
    : orientationType.PORTRAIT;
}

// Shared device / capability detection so callers (VideoJs, Experience) don't each
// re-implement it and drift apart.
export function isIOS(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

// A phone-scale frame whose primary pointer is touch, i.e. one that types on an
// on-screen keyboard. The frame alone isn't enough: an MCP App card or an inline
// embed on a desktop is phone-scale too, and a touchscreen laptop has touch
// points but a fine primary pointer.
export function isPhoneScaleCoarse(size?: Size | null): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches &&
    isPhoneScale(size)
  );
}

// True when the browser exposes the real element Fullscreen API (Android, desktop;
// false on iOS Safari, which has no element FS — it uses pseudo-fullscreen).
export function hasElementFullscreen(): boolean {
  return (
    typeof document !== "undefined" &&
    !!(
      document.fullscreenEnabled ||
      (document as Document & { webkitFullscreenEnabled?: boolean })
        .webkitFullscreenEnabled
    )
  );
}

const listeners: OrientationListener[] = [];
let monitoring = false;
let releasing = false;
let releaseTimer: number | null = null;
let orientation: Orientation = calcOrientation();

function onFocusIn(e: FocusEvent) {
  if (
    !isTextField(e.target as HTMLInputElement | null) ||
    !isPhoneScaleCoarse()
  ) {
    return;
  }
  cancelRelease();
  if (!heldOrientation) {
    heldOrientation = orientation;
  }
}

// The keyboard is still closing here and the viewport still shrunk, so the
// hold stays until a resize shows the frame's shape again (see onResize).
function onFocusOut() {
  if (!heldOrientation) {
    return;
  }
  cancelRelease();
  releasing = true;
  releaseTimer = setTimeout(release, KEYBOARD_CLOSE_MS);
}

function cancelRelease() {
  releasing = false;
  clearTimeout(releaseTimer as number);
  releaseTimer = null;
}

function release() {
  if (!heldOrientation) {
    return;
  }
  cancelRelease();
  heldOrientation = null;
  refresh();
}

function calcOrientation(): Orientation {
  // Pseudo-fullscreen forces the immersive landscape layout even on a
  // physically-portrait phone. The shop-window / LandscapeNav open+close logic is
  // gated on isLandscape(), so report LANDSCAPE while it's on — otherwise tabs
  // won't open and the sheet won't close. Survives real resizes the same way.
  if (typeof window !== "undefined" && window.__pixPseudoFs) {
    return orientationType.LANDSCAPE;
  }
  // The MCP landscape surface is a video-first landscape card whose
  // sheet open/close is gated on isLandscape(). Its card grows to fit the tabs
  // and the last-step chain, so on a desktop MCP card it can become taller than
  // wide — which would otherwise flip this to PORTRAIT on the resize and stop
  // the tabs opening/closing the overlay (the same square-boundary hazard the
  // desktop-resize guard in appController warns about). Force LANDSCAPE for it.
  if (isMcpLandscapeLayout()) {
    return orientationType.LANDSCAPE;
  }
  if (heldOrientation) {
    return heldOrientation;
  }
  return sizeOrientation(sizeManager.getSize());
}

// Recompute + notify (used when pseudo-fullscreen toggles, since that
// changes calcOrientation's result without a viewport resize).
export function refresh() {
  setOrientation(calcOrientation());
}

export function monitorSize(value: boolean) {
  if (value) {
    if (!monitoring) {
      sizeManager.addListener(onResize);
      monitoring = true;
    }
  } else if (monitoring) {
    sizeManager.removeListener(onResize);
    monitoring = false;
  }
}

export function addListener(listener: OrientationListener) {
  listeners.push(listener);
}

export function removeListener(listener: OrientationListener) {
  arrayUtils.remove(listeners, listener);
}

export function getOrientation(): Orientation {
  return orientation;
}

export function setOrientation(next: Orientation) {
  if (next !== orientation) {
    orientation = next;
    notifyListeners();
  }
}

function notifyListeners() {
  for (let n = 0, max = listeners.length; n < max; n++) {
    listeners[n](orientation);
  }
}

function onResize() {
  if (releasing && sizeOrientation(sizeManager.getSize()) === heldOrientation) {
    release();
    return;
  }
  setOrientation(calcOrientation());
}

monitorSize(true);
if (typeof document !== "undefined") {
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
}
if (typeof window !== "undefined") {
  window.addEventListener("orientationchange", release);
}
